import { useEffect, useMemo, useRef, useState } from "react"

import { Avatar } from "@atoms"

import { UserAvatarStack } from "@molecules"

import { Table } from "@organisms"

import styles from "./UserAvatarCell.module.scss"
import { UserAvatarCellProps } from "./UserAvatarCell.types"

export default function UserAvatarCell(props: UserAvatarCellProps) {
    const { users, isLoading } = props

    const containerRef = useRef<HTMLDivElement | null>(null)
    const [limit, setLimit] = useState<number>(users.length)

    const avatarSizePx = 28
    const overlapRatio = 0.2

    const noAvatars = users.length === 0

    const computeLimit = useMemo(() => {
        return (containerWidth: number) => {
            const firstWidth = avatarSizePx
            const additionalWidth = avatarSizePx * (1 - overlapRatio)

            if (containerWidth <= firstWidth) {
                return 1
            } else {
                const remaining = containerWidth - firstWidth
                const extra = Math.floor(remaining / additionalWidth)
                return Math.max(1, extra + 1)
            }
        }
    }, [])

    useEffect(() => {
        const element = containerRef.current
        if (!element) {
            return
        } else {
            const update = () => {
                const computedStyle = window.getComputedStyle(element)
                const paddingLeft = parseFloat(computedStyle.paddingLeft) || 0
                const paddingRight = parseFloat(computedStyle.paddingRight) || 0
                const availableWidth = Math.max(0, element.clientWidth - paddingLeft - paddingRight)
                const calculated = computeLimit(availableWidth)
                setLimit(calculated)
            }

            update()

            const resizeObserver = new ResizeObserver(() => update())
            resizeObserver.observe(element)

            return () => {
                resizeObserver.disconnect()
            }
        }
    }, [computeLimit])

    if (noAvatars) {
        return <Table.Cells.NoValueCell />
    } else {
        return (
            <div className={styles.base} ref={containerRef}>
                {isLoading ? (
                    <Avatar variant="user-unknown" size="md" />
                ) : (
                    <UserAvatarStack
                        users={users}
                        limit={Math.max(1, Math.min(limit, users.length))}
                        avatarSize="md"
                    />
                )}
            </div>
        )
    }
}
